import { Package, Sparkles } from 'lucide-react';
import { useAccess } from '../access';
import { useEffect, useState } from 'react';
import { AccountService } from '../../api/account';
import { SubscriptionPlanItem, SubscriptionStatusResponse } from '../../api/account/types';
import { CurrentSubscriptionCard } from './CurrentSubscriptionCard';
import { getAthenaUrl } from '../../config/platformUrls';

// Plan name mapping for display
const PLAN_NAMES: Record<string, string> = {
  base: 'Base',
  pro: 'Core',
  plus: 'Premium',
};

type PlanTier = 'base' | 'pro' | 'plus' | 'other';

export function SubscriptionPlans() {
  const { isLoading } = useAccess();
  const [actionLoading, setActionLoading] = useState<PlanTier | null>(null);
  const [actionError, setActionError] = useState<Map<PlanTier, string | null>>(new Map());
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlanItem | null>(null);
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionStatusResponse | null>(
    null,
  );

  useEffect(() => {
    let mounted = true;

    AccountService.getCurrentSubscription()
      .then((response) => {
        if (mounted) {
          setCurrentSubscription(response);
          setCurrentPlan(response?.plan ?? null);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch current subscription:', error);
      })
      .finally(() => {
        if (mounted) setIsPriceLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading indicator if any data is still loading
  if (isLoading || isPriceLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center items-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E34290]"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is on a custom subscription plan
  const isCustomSubscription = currentSubscription?.plan?.is_custom === true;
  const isDeprecatedSubscription = currentSubscription?.plan?.is_deprecated === true;

  // Determine user state
  const currentTier: PlanTier =
    currentPlan?.tier === 'base' || currentPlan?.tier === 'pro' || currentPlan?.tier === 'plus'
      ? currentPlan.tier
      : 'other';
  const hasCancellationScheduled = !!currentSubscription?.end_date;

  // Helper to handle cancellation
  const handleCancel = async () => {
    setActionLoading(currentTier);
    setActionError((prev) => new Map(prev).set(currentTier, null));

    try {
      const response = await AccountService.cancelSubscription({
        return_url: window.location.origin + '/account',
      });
      window.location.href = response.url;
    } catch (err: any) {
      setActionError((prev) =>
        new Map(prev).set(currentTier, err.message || 'Failed to open Stripe portal'),
      );
      setActionLoading(null);
    }
  };

  // Helper to handle reactivation
  const handleReactivate = async () => {
    setActionLoading(currentTier);
    setActionError((prev) => new Map(prev).set(currentTier, null));
    try {
      await AccountService.reactivateSubscription();
      window.location.reload();
    } catch (err: any) {
      setActionError((prev) =>
        new Map(prev).set(currentTier, err.message || 'Failed to reactivate subscription'),
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Determine current plan name for display
  const getCurrentPlanName = (): string => {
    if (currentTier === 'other') {
      return currentPlan?.tier || 'Custom';
    }
    return PLAN_NAMES[currentTier] || currentPlan?.tier || 'Unknown';
  };

  const hasActiveSubscription = currentPlan !== null;

  return (
    <div className="space-y-4">
      {/* Athena Upgrade Banner - Always show */}
      <div className="p-6 border-2 border-[#E34290] bg-gradient-to-r from-[#E34290]/5 to-[#E34290]/10 dark:from-[#E34290]/10 dark:to-[#E34290]/20 rounded-lg">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Sparkles className="h-6 w-6 text-[#E34290]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Upgrade to Athena
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Experience the next generation of Jua with Athena. Get access to our latest AI weather
              models, enhanced features, and improved performance.
            </p>
            <a
              href={getAthenaUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E34290] text-white font-medium rounded-lg hover:bg-[#E34290]/90 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Get Started with Athena →
            </a>
          </div>
        </div>
      </div>

      {/* Custom Subscription Message */}
      {isCustomSubscription && (
        <div className="p-6 border-2 border-[#E34290] bg-[#E34290]/5 dark:bg-[#E34290]/10 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Package className="h-6 w-6 text-[#E34290]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Custom Subscription Plan
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                You are currently on a custom subscription plan tailor-made for your specific needs.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                To manage your subscription or for any help, please reach out to our support team at{' '}
                <a
                  href="mailto:support@jua.ai"
                  className="text-[#E34290] hover:underline font-medium"
                >
                  support@jua.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Deprecated Subscription Message */}
      {isDeprecatedSubscription && (
        <div className="p-6 border-2 border-orange-500 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Package className="h-6 w-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Deprecated Subscription Plan
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                You are currently on a deprecated subscription plan. While your plan will continue
                to work, we recommend upgrading to Athena for the latest features and better value.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                For any questions about upgrading, please contact our support team at{' '}
                <a
                  href="mailto:support@jua.ai"
                  className="text-[#E34290] hover:underline font-medium"
                >
                  support@jua.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Management - Only show if user has an active subscription */}
      {hasActiveSubscription && !isCustomSubscription && (
        <CurrentSubscriptionCard
          planName={getCurrentPlanName()}
          currency={currentPlan?.currency}
          price={currentPlan?.price}
          isCancelled={hasCancellationScheduled}
          endDate={currentSubscription?.end_date}
          error={actionError.get(currentTier) ?? null}
          buttonLoading={actionLoading === currentTier}
          buttonDisabled={!!actionLoading}
          onCancel={handleCancel}
          onReactivate={handleReactivate}
        />
      )}

      {/* No Subscription Message */}
      {!hasActiveSubscription && (
        <div className="p-6 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Active Subscription
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You don't have an active subscription. To get started with Jua, please visit Athena
                to explore our latest plans and features.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
